/**
 * After the walk: the short sheet on the visit.
 *
 * What he recommended, and which bid he will send — a reach_out reminder drops on the customer (My Day, Needs
 * Attention) so the follow-up bid is never forgotten. "Make the bid they sign" already lives on the customer card
 * (PlanBidButton), so the sheet links there instead of carrying a second copy. Shown only when the visit is a
 * lawn walkthrough (services/walkthrough); a plain quote visit never sees it.
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import { api } from '../services/api';
import { WHEN } from './ReminderAdd';

export default function WalkthroughFinish({ jobId, onSaved }: { jobId: number | string; onSaved?: () => void }) {
  const qc = useQueryClient();
  const { data } = useQuery(['walkthrough-visit', String(jobId)], () => api.get(`/walkthrough/visit/${jobId}`).then(r => r.data?.walkthrough || null));
  const [recommended, setRecommended] = useState('');
  const [step, setStep] = useState('plan_bid');
  const [when, setWhen] = useState('tomorrow');
  useEffect(() => { if (data) { setRecommended(data.recommended || ''); if (data.next_step) setStep(data.next_step); } }, [data]);
  const save = useMutation(() => api.post(`/walkthrough/visit/${jobId}/done`, { recommended, next_step: step, when }).then(r => r.data), {
    onSuccess: (d: any) => {
      toast.success(d?.message || 'Saved');
      qc.invalidateQueries(['walkthrough-visit', String(jobId)]); qc.invalidateQueries(['quote-visit', String(jobId)]);
      qc.invalidateQueries('bid-visits'); qc.invalidateQueries('field-stops'); qc.invalidateQueries('calendar-jobs'); qc.invalidateQueries('day-clock');
      onSaved?.();
    },
    onError: (e: any) => { toast.error(e?.response?.data?.message || 'Could not save that'); },
  });
  if (!data) return null;
  const chip = (on: boolean) => `px-2.5 py-1 rounded-full border text-xs ${on ? 'press bg-brand-600 text-black border-brand-600' : 'bg-white text-gray-700 border-gray-300'}`;
  const toCard = `/customers?profile=${data.customer_id}`;

  if (data.status === 'done') {
    const stepWord = (data.next_steps || []).find((s: any) => s.key === data.next_step)?.label || 'Nothing for now';
    return (
      <div className="card p-4 space-y-2">
        <p className="text-xs font-semibold text-gray-600">After the walk</p>
        {data.recommended && <p className="text-sm text-gray-900">{data.recommended}</p>}
        <p className="text-sm text-gray-700">Next: <b>{stepWord}</b>{data.reminder_id ? ' — the reminder is on the customer.' : '.'}</p>
        <Link to={toCard} className="btn-primary w-full justify-center text-sm py-3">Open {data.customer_name} — make the bid they sign</Link>
      </div>
    );
  }
  return (
    <div className="card p-4 space-y-3">
      <div>
        <p className="text-xs font-semibold text-gray-600">After the walk</p>
        <p className="text-sm text-gray-700">A free lawn walkthrough with {data.customer_name}. Write down what you recommended and what you will send — the reminder makes sure the bid goes out.</p>
      </div>
      <textarea value={recommended} onChange={e => setRecommended(e.target.value)} rows={3} className="input text-sm w-full"
        placeholder="Fall pre-emergent now, a top dressing on the front in spring, raise the cut to 3.5 in." />
      <div>
        <p className="text-xs font-medium text-gray-600 mb-1">What you will send</p>
        <div className="flex flex-wrap gap-1.5">
          {(data.next_steps || []).map((s: any) => <button key={s.key} type="button" onClick={() => setStep(s.key)} className={chip(step === s.key)}>{s.label}</button>)}
        </div>
      </div>
      {step !== 'nothing' && (
        <div>
          <p className="text-xs font-medium text-gray-600 mb-1">Remind me</p>
          <div className="flex flex-wrap gap-1.5">
            {WHEN.filter(w => ['tomorrow', 'week'].includes(w.key)).map(w => <button key={w.key} type="button" onClick={() => setWhen(w.key)} className={chip(when === w.key)}>{w.label}</button>)}
          </div>
        </div>
      )}
      <button type="button" onClick={() => save.mutate()} disabled={save.isLoading || (!recommended.trim() && step === 'nothing')} className="btn-primary w-full justify-center text-sm py-3 disabled:opacity-50">
        {save.isLoading ? 'Saving…' : 'Save and close the walk'}
      </button>
      <p className="text-[11px] text-gray-500">Closing here drafts no bid by itself. <Link to={toCard} className="underline font-semibold">Open the customer</Link> to make the plan bid they sign.</p>
    </div>
  );
}
